package com.ives.utils.math;

class Quaternion {
	public var s:Float;
	public var x:Float;
	public var y:Float;
	public var z:Float;

	public function new(s:Float = 0, x:Float = 0, y:Float = 0, z:Float = 0){
		this.s = s;
		this.x = x;
		this.y = y;
		this.z = z;
	}

	public function toArray():Array<Float>{
		return [s, x, y, z];
	}

	public function fromArray(array:Array<Float>, offset:Int = 0):Quaternion{
		s = array[offset + 0];
		x = array[offset + 1];
		y = array[offset + 2];
		z = array[offset + 3];

		return this;
	}

	public function toAxisAngle():AxisAngle{
		if (s > 1)
			normalize(); // if s>1 acos and sqrt will produce errors, this cant happen if quaternion is normalised

		var axis:Vector3D = new Vector3D();
		var angle:Float = 2 * Math.acos(s);

		var temp:Float = Math.sqrt(1 - s*s); // assuming quaternion normalised then w is less than 1, so term always positive.

		if (temp < 0.001) { // test to avoid divide by zero, s is always positive due to sqrt
			// if s close to zero then direction of axis not important
			axis.x = x; // if it is important that axis is normalised then replace with x=1; y=z=0;
			axis.y = y;
			axis.z = z;
		} else {
			axis.x = x / temp; // normalise axis
			axis.y = y / temp;
			axis.z = z / temp;
		}

		return new AxisAngle(axis, angle);
	}

	public static function fromAxisAngle(axis:Vector3D, angle:Float):Quaternion{
		var cosHalfAngle = Math.cos(angle / 2.0);
		var sinHalfAngle = Math.sin(angle / 2.0);

		return new Quaternion(cosHalfAngle, sinHalfAngle * axis.x, sinHalfAngle * axis.y, sinHalfAngle * axis.z);
	}

	public function rotate(vector:Vector3D):Vector3D{
		normalize();

		var u:Vector3D = vector.clone();

		var a = 2.0 * (x * u.x + y * u.y + z * u.z);
		var b = s * s - x * x - y * y - z * z;
		var c = 2.0 * s;

		vector.x = a * x + b * u.x + c * (y * u.z - z * u.y);
		vector.y = a * y + b * u.y + c * (z * u.x - x * u.z);
		vector.z = a * z + b * u.z + c * (x * u.y - y * u.x);

		return vector;
	}

	public function length2():Float{
		return s * s + x * x + y * y + z * z;
	}

	public function length():Float{
		return Math.sqrt(length2());
	}

	public function scale(scale:Float):Quaternion{
		this.x *= scale;
		this.y *= scale;
		this.z *= scale;
		this.s *= scale;

		return this;
	}

	public function normalize():Quaternion{
		var length2:Float = length2();

		if(length2 > 0)
			scale(1 / Math.sqrt(length2));

		return this;
	}

	public function multiply(b:Quaternion):Quaternion{
		var a:Quaternion = clone();

		s = a.s * b.s - a.x * b.x - a.y * b.y - a.z * b.z;
		x = a.s * b.x + b.s * a.x + a.y * b.z - a.z * b.y;
		y = a.s * b.y + b.s * a.y + a.z * b.x - a.x * b.z;
		z = a.s * b.z + b.s * a.z + a.x * b.y - a.y * b.x;

		return this;
	}

	public function clone():Quaternion {
		return new Quaternion(s, x, y, z);
	}

	public static function identity():Quaternion{
		return new Quaternion(1, 0, 0, 0);
	}

	public function toString():String{
		return '${s},${x},${y},${z}';
	}
}
